/**
 * Venda pelo chat: busca no estoque do ERP, carrinho, desconto, entrega,
 * pagamento e criação do pedido no ERP — sem sair da conversa. O carrinho é
 * salvo sozinho (a conversa pode ser retomada por outra pessoa da equipe).
 */
import type {
  ConversaResumo,
  EntregaVenda,
  ItemVenda,
  OpcoesVenda,
  ProdutoErp,
  RascunhoVenda,
  Venda,
} from "@ydchat/shared";
import { ROTULO_STATUS_PEDIDO, calcularTotais } from "@ydchat/shared";
import {
  AlertTriangle,
  Loader2,
  Minus,
  PackageCheck,
  Plus,
  Search,
  Send,
  ShoppingBag,
  Store,
  Trash2,
  Truck,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Botao } from "@/components/ui/botao";
import { Campo } from "@/components/ui/campo";
import { Esqueleto } from "@/components/ui/esqueleto";
import { PainelLateral } from "@/components/ui/painel-lateral";
import { Rotulo, Seletor } from "@/components/ui/seletor";
import { Selo } from "@/components/ui/selo";
import { ErroApi } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatarData, formatarMoeda } from "@/lib/formatar";
import { useEnviarProduto, useProdutosEnviados } from "@/lib/vitrine";
import {
  useAcoesVenda,
  useOpcoesVenda,
  useProdutos,
  useVendas,
  type FiltroProduto,
} from "@/lib/vendas";
import { STATUS_PEDIDO } from "./contexto-cliente";

const ENTREGA_PADRAO: EntregaVenda = {
  modo: "retirada",
  transportadorId: null,
  transportadorNome: null,
  frete: 0,
  endereco: null,
};

export function PainelVenda({
  conversa,
  aberto,
  onFechar,
  buscaInicial = "",
}: {
  conversa: ConversaResumo;
  aberto: boolean;
  onFechar: () => void;
  /** Ex.: nome do produto identificado na foto da cliente. */
  buscaInicial?: string;
}) {
  return (
    <PainelLateral aberto={aberto} onFechar={onFechar} titulo="Venda">
      {aberto && (
        <ConteudoVenda key={buscaInicial} conversa={conversa} buscaInicial={buscaInicial} />
      )}
    </PainelLateral>
  );
}

function ConteudoVenda({
  conversa,
  buscaInicial,
}: {
  conversa: ConversaResumo;
  buscaInicial: string;
}) {
  const vendas = useVendas(conversa.id);
  const opcoes = useOpcoesVenda(conversa.id, true);
  const { salvar, confirmar, descartar } = useAcoesVenda(conversa.id);
  const salvarCarrinho = salvar.mutate;
  const [r, setR] = useState<RascunhoVenda | null>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const carregado = useRef(false);

  // Primeira carga: o carrinho do servidor vira o estado local.
  useEffect(() => {
    if (carregado.current || !vendas.data) return;
    carregado.current = true;
    const c = vendas.data.carrinho;
    setR(
      c
        ? {
            itens: c.itens,
            desconto: c.desconto,
            entrega: c.entrega,
            formaPagamento: c.formaPagamento,
            observacao: c.observacao,
            vendedoraErpId: c.vendedoraErpId,
          }
        : {
            itens: [],
            desconto: 0,
            entrega: ENTREGA_PADRAO,
            formaPagamento: null,
            observacao: null,
            vendedoraErpId: null,
          },
    );
  }, [vendas.data]);

  // Salva sozinho 600ms depois da última mudança.
  const ultimoSalvo = useRef<string>("");
  useEffect(() => {
    if (!r) return;
    const json = JSON.stringify(r);
    if (json === ultimoSalvo.current) return;
    const vazio = !r.itens.length && !vendas.data?.carrinho;
    if (vazio) return;
    const t = setTimeout(() => {
      ultimoSalvo.current = json;
      salvarCarrinho(r, {
        onSuccess: () => setErroSalvar(null),
        onError: (e) =>
          setErroSalvar(e instanceof ErroApi ? e.message : "Não foi possível salvar o carrinho."),
      });
    }, 600);
    return () => clearTimeout(t);
  }, [r, salvarCarrinho, vendas.data?.carrinho]);

  if (vendas.isPending || !r) return <Esqueleto className="h-64" />;
  if (vendas.isError) return <p className="text-sm text-danger-500">{vendas.error.message}</p>;
  if (opcoes.isError) {
    return (
      <p className="flex items-start gap-2 rounded-md bg-warning-100 p-3 text-sm text-warning-500">
        <AlertTriangle size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" />{" "}
        {opcoes.error.message}
      </p>
    );
  }

  const carrinho = vendas.data.carrinho;
  const enviando = carrinho?.status === "enviando" || confirmar.isPending;
  const t = calcularTotais(r);
  const limite = opcoes.data?.descontoMaximoPercentual ?? null;
  const acimaDoLimite = limite !== null && t.percentualDesconto > limite + 0.001;
  const mudar = (p: Partial<RascunhoVenda>) => setR({ ...r, ...p });

  const adicionar = (p: ProdutoErp, v: ProdutoErp["variacoes"][number]) => {
    const existente = r.itens.find((i) => i.variacaoId === v.id);
    if (existente) {
      mudar({
        itens: r.itens.map((i) =>
          i.variacaoId === v.id
            ? { ...i, quantidade: Math.min(i.quantidade + 1, v.estoqueDisponivel) }
            : i,
        ),
      });
      return;
    }
    const novo: ItemVenda = {
      variacaoId: v.id,
      produtoId: p.id,
      nome: p.nome,
      sku: v.sku,
      cor: v.cor,
      tamanho: v.tamanho,
      fotoUrl: v.fotoUrl ?? p.fotoUrl,
      preco: v.preco,
      quantidade: 1,
      desconto: 0,
      estoqueDisponivel: v.estoqueDisponivel,
    };
    mudar({ itens: [...r.itens, novo] });
  };

  return (
    <div className="space-y-6">
      {carrinho?.status === "erro" && carrinho.erro && (
        <p className="flex items-start gap-2 rounded-md bg-danger-100 p-3 text-sm text-danger-500">
          <AlertTriangle size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" /> O ERP recusou o
          pedido: {carrinho.erro}
        </p>
      )}
      {carrinho?.status === "enviando" && (
        <p className="flex items-center gap-2 rounded-md bg-gold-50 p-3 text-sm text-gold-800">
          <Loader2 size={16} className="animate-spin" /> Criando o pedido no ERP. Se o ERP estiver
          fora, ele é criado assim que voltar.
        </p>
      )}

      <BuscaProdutos conversaId={conversa.id} buscaInicial={buscaInicial} onAdicionar={adicionar} />

      <section aria-label="Carrinho" className="space-y-3">
        <h3 className="flex items-center gap-2 text-sm">
          <ShoppingBag size={16} strokeWidth={1.75} /> Carrinho
        </h3>
        {!r.itens.length ? (
          <p className="rounded-lg border border-dashed border-neutral-300 p-4 text-center text-sm text-neutral-500">
            Busque e toque num tamanho para adicionar.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
            {r.itens.map((i) => (
              <LinhaCarrinho
                key={i.variacaoId}
                item={i}
                onMudar={(novo) =>
                  mudar({ itens: r.itens.map((x) => (x.variacaoId === i.variacaoId ? novo : x)) })
                }
                onRemover={() =>
                  mudar({ itens: r.itens.filter((x) => x.variacaoId !== i.variacaoId) })
                }
              />
            ))}
          </ul>
        )}
      </section>

      {!!r.itens.length && (
        <>
          <section aria-label="Entrega" className="space-y-3">
            <h3 className="text-sm">Entrega</h3>
            <div className="grid grid-cols-2 gap-2" role="radiogroup">
              {(["retirada", "entrega"] as const).map((modo) => (
                <button
                  key={modo}
                  role="radio"
                  aria-checked={r.entrega.modo === modo}
                  onClick={() =>
                    mudar({
                      entrega: modo === "retirada" ? ENTREGA_PADRAO : { ...r.entrega, modo },
                    })
                  }
                  className={cn(
                    "flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors",
                    r.entrega.modo === modo
                      ? "border-gold-500 bg-gold-50 text-gold-800"
                      : "border-neutral-200 text-neutral-600 hover:bg-neutral-50",
                  )}
                >
                  {modo === "retirada" ? (
                    <Store size={16} strokeWidth={1.75} />
                  ) : (
                    <Truck size={16} strokeWidth={1.75} />
                  )}
                  {modo === "retirada" ? "Retirada na loja" : "Entrega"}
                </button>
              ))}
            </div>
            {r.entrega.modo === "entrega" && (
              <div className="space-y-3">
                <div className="grid grid-cols-[1fr_110px] gap-2">
                  <Rotulo texto="Como entregar">
                    <Seletor
                      value={r.entrega.transportadorId ?? ""}
                      onChange={(e) => {
                        const tr = opcoes.data?.transportadores.find(
                          (x) => x.id === e.target.value,
                        );
                        mudar({
                          entrega: {
                            ...r.entrega,
                            transportadorId: tr?.id ?? null,
                            transportadorNome: tr?.nome ?? null,
                          },
                        });
                      }}
                    >
                      <option value="">Escolha</option>
                      {opcoes.data?.transportadores.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.nome}
                        </option>
                      ))}
                    </Seletor>
                  </Rotulo>
                  <Rotulo texto="Frete (R$)">
                    <CampoValor
                      valor={r.entrega.frete}
                      onMudar={(frete) => mudar({ entrega: { ...r.entrega, frete } })}
                    />
                  </Rotulo>
                </div>
                <EnderecoEntrega
                  entrega={r.entrega}
                  onMudar={(endereco) => mudar({ entrega: { ...r.entrega, endereco } })}
                />
              </div>
            )}
          </section>

          <section aria-label="Pagamento" className="space-y-3">
            <div className="grid grid-cols-[1fr_130px] gap-2">
              <Rotulo texto="Forma de pagamento">
                <Seletor
                  value={r.formaPagamento ?? ""}
                  onChange={(e) => mudar({ formaPagamento: e.target.value || null })}
                >
                  <option value="">A combinar</option>
                  {opcoes.data?.formasPagamento.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </Seletor>
              </Rotulo>
              <Rotulo texto="Desconto (R$)" ajuda={limite !== null ? `Até ${limite}%` : undefined}>
                <CampoValor
                  valor={r.desconto}
                  onMudar={(desconto) => mudar({ desconto })}
                  invalido={acimaDoLimite}
                />
              </Rotulo>
            </div>
            <CampoVendedora
              valor={r.vendedoraErpId}
              opcoes={opcoes.data}
              onMudar={(vendedoraErpId) => mudar({ vendedoraErpId })}
            />
            <Rotulo texto="Observação para o ERP">
              <Campo
                value={r.observacao ?? ""}
                onChange={(e) => mudar({ observacao: e.target.value || null })}
                maxLength={500}
                placeholder="Ex.: embrulhar para presente"
              />
            </Rotulo>
          </section>

          <section aria-label="Total" className="space-y-1.5 rounded-xl bg-neutral-50 p-4 text-sm">
            <LinhaTotal rotulo="Produtos" valor={t.bruto} />
            {t.bruto !== t.subtotal && (
              <LinhaTotal rotulo="Desconto nos itens" valor={t.subtotal - t.bruto} />
            )}
            {!!t.desconto && <LinhaTotal rotulo="Desconto" valor={-t.desconto} />}
            {r.entrega.modo === "entrega" && <LinhaTotal rotulo="Frete" valor={r.entrega.frete} />}
            <div className="flex items-baseline justify-between border-t border-neutral-200 pt-2">
              <span className="font-semibold text-neutral-900">Total</span>
              <span className="font-display text-2xl font-bold text-neutral-900">
                {formatarMoeda(t.total)}
              </span>
            </div>
            {acimaDoLimite && (
              <p className="text-[13px] text-danger-500">
                O desconto passa de {limite}%. Peça para a gerência aplicar.
              </p>
            )}
            {erroSalvar && !acimaDoLimite && (
              <p className="text-[13px] text-danger-500">{erroSalvar}</p>
            )}
          </section>

          {!conversa.contato.erpClienteId && (
            <p className="rounded-md bg-gold-50 p-3 text-[13px] text-gold-800">
              Para criar o pedido, cadastre a cliente no ERP pelo painel do cliente.
            </p>
          )}
          <div className="flex gap-2">
            <Botao
              className="flex-1 justify-center"
              disabled={
                enviando ||
                acimaDoLimite ||
                !conversa.contato.erpClienteId ||
                salvar.isPending ||
                (r.entrega.modo === "entrega" && !r.entrega.transportadorId)
              }
              onClick={() =>
                confirmar.mutate(undefined, {
                  // Virou pedido: o carrinho recomeça vazio (o pedido aparece em "Pedidos desta conversa").
                  onSuccess: () => {
                    const vazio = {
                      itens: [],
                      desconto: 0,
                      entrega: ENTREGA_PADRAO,
                      formaPagamento: null,
                      observacao: null,
                      vendedoraErpId: null,
                    };
                    ultimoSalvo.current = JSON.stringify(vazio);
                    setR(vazio);
                  },
                })
              }
            >
              {enviando ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <PackageCheck size={16} strokeWidth={1.75} />
              )}
              Criar pedido no ERP
            </Botao>
            {carrinho && carrinho.status !== "enviando" && (
              <Botao
                variante="fantasma"
                onClick={() =>
                  descartar.mutate(undefined, {
                    onSuccess: () => {
                      ultimoSalvo.current = "";
                      setR({
                        itens: [],
                        desconto: 0,
                        entrega: ENTREGA_PADRAO,
                        formaPagamento: null,
                        observacao: null,
                        vendedoraErpId: null,
                      });
                    },
                  })
                }
                aria-label="Descartar carrinho"
                title="Descartar carrinho"
              >
                <Trash2 size={16} strokeWidth={1.75} />
              </Botao>
            )}
          </div>
          <p className="text-xs text-neutral-500">
            O pedido entra no ERP em aberto, com pagamento pendente e as peças reservadas. Preço e
            estoque são conferidos pelo ERP.
          </p>
        </>
      )}

      {!!vendas.data.pedidos.length && <PedidosCriados pedidos={vendas.data.pedidos} />}
      <ProdutosEnviados conversaId={conversa.id} />
    </div>
  );
}

function BuscaProdutos({
  conversaId,
  buscaInicial,
  onAdicionar,
}: {
  conversaId: string;
  buscaInicial: string;
  onAdicionar: (p: ProdutoErp, v: ProdutoErp["variacoes"][number]) => void;
}) {
  const [texto, setTexto] = useState(buscaInicial);
  const [f, setF] = useState<FiltroProduto>({
    texto: "",
    tamanho: "",
    cor: "",
    somenteComEstoque: true,
  });
  useEffect(() => {
    const t = setTimeout(() => setF((x) => ({ ...x, texto: texto.trim() })), 300);
    return () => clearTimeout(t);
  }, [texto]);
  const produtos = useProdutos(conversaId, f);
  const enviar = useEnviarProduto(conversaId);
  const ativo = f.texto.length >= 2 || !!f.tamanho || !!f.cor;

  return (
    <section aria-label="Adicionar produto" className="space-y-2">
      <label className="relative block">
        <Search
          size={16}
          strokeWidth={1.75}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
        />
        <Campo
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Adicionar produto: nome, SKU ou código"
          className="pl-9"
          aria-label="Buscar produto"
        />
      </label>
      <div className="flex flex-wrap items-center gap-2 text-[13px]">
        <Campo
          value={f.tamanho}
          onChange={(e) => setF({ ...f, tamanho: e.target.value })}
          placeholder="Tamanho"
          className="h-8 w-24 text-[13px]"
          aria-label="Tamanho"
        />
        <Campo
          value={f.cor}
          onChange={(e) => setF({ ...f, cor: e.target.value })}
          placeholder="Cor"
          className="h-8 w-28 text-[13px]"
          aria-label="Cor"
        />
        <label className="flex items-center gap-1.5 text-neutral-600">
          <input
            type="checkbox"
            checked={f.somenteComEstoque}
            onChange={(e) => setF({ ...f, somenteComEstoque: e.target.checked })}
            className="size-4 accent-[var(--color-gold-500)]"
          />
          Só com estoque
        </label>
      </div>
      {ativo &&
        (produtos.isPending ? (
          <Esqueleto className="h-20" />
        ) : produtos.isError ? (
          <p className="text-sm text-danger-500">{produtos.error.message}</p>
        ) : !produtos.data.length ? (
          <p className="text-sm text-neutral-500">Nada encontrado no estoque do ERP.</p>
        ) : (
          <ul className="rolagem-fina max-h-80 space-y-2 overflow-y-auto">
            {produtos.data.map((p) => (
              <li key={p.id} className="flex gap-3 rounded-lg border border-neutral-200 p-2.5">
                <Foto url={p.fotoUrl} nome={p.nome} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-neutral-900">{p.nome}</p>
                      <p className="font-display text-sm font-bold text-neutral-900">
                        {formatarMoeda(Math.min(...p.variacoes.map((v) => v.preco)))}
                        {p.urlLoja && (
                          <span className="ml-1.5 align-middle font-sans text-[11px] font-semibold text-success-500">
                            no site
                          </span>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => enviar.mutate(p.id)}
                      disabled={enviar.isPending}
                      title={
                        p.urlLoja
                          ? "Enviar à cliente (foto, preço e link do site)"
                          : "Enviar à cliente (foto e preço)"
                      }
                      aria-label={`Enviar ${p.nome} à cliente`}
                      className="flex shrink-0 items-center gap-1 rounded-md border border-gold-300 px-2 py-1 text-[12px] font-semibold text-gold-800 hover:bg-gold-50 disabled:opacity-50"
                    >
                      <Send size={13} strokeWidth={1.75} /> Enviar
                    </button>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {p.variacoes.map((v) => (
                      <button
                        key={v.id}
                        disabled={v.estoqueDisponivel <= 0}
                        onClick={() => onAdicionar(p, v)}
                        title={`${v.estoqueDisponivel} disponível${v.sku ? ` · ${v.sku}` : ""}`}
                        className="rounded-md border border-neutral-300 px-2 py-1 text-[12px] font-semibold text-neutral-800 hover:border-gold-500 hover:bg-gold-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:line-through"
                      >
                        {[v.tamanho, v.cor].filter(Boolean).join(" · ") || v.sku || "Único"}
                        <span className="ml-1 font-normal text-neutral-500">
                          ({v.estoqueDisponivel})
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ))}
    </section>
  );
}

function LinhaCarrinho({
  item: i,
  onMudar,
  onRemover,
}: {
  item: ItemVenda;
  onMudar: (i: ItemVenda) => void;
  onRemover: () => void;
}) {
  const qtd = (n: number) =>
    onMudar({ ...i, quantidade: Math.max(1, Math.min(n, Math.max(i.estoqueDisponivel, 1))) });
  return (
    <li className="flex gap-3 p-2.5">
      <Foto url={i.fotoUrl} nome={i.nome} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-neutral-900">{i.nome}</p>
        <p className="text-xs text-neutral-500">
          {[i.tamanho, i.cor].filter(Boolean).join(" · ")}
          {i.sku ? ` · ${i.sku}` : ""}
        </p>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <div className="flex items-center rounded-md border border-neutral-200">
            <button
              onClick={() => qtd(i.quantidade - 1)}
              aria-label="Menos um"
              className="flex size-8 items-center justify-center text-neutral-600 hover:bg-neutral-100"
            >
              <Minus size={14} strokeWidth={2} />
            </button>
            <span className="w-7 text-center text-sm font-semibold" aria-live="polite">
              {i.quantidade}
            </span>
            <button
              onClick={() => qtd(i.quantidade + 1)}
              disabled={i.quantidade >= i.estoqueDisponivel}
              aria-label="Mais um"
              className="flex size-8 items-center justify-center text-neutral-600 hover:bg-neutral-100 disabled:opacity-40"
            >
              <Plus size={14} strokeWidth={2} />
            </button>
          </div>
          <span className="font-display text-sm font-bold text-neutral-900">
            {formatarMoeda(i.preco * i.quantidade - i.desconto)}
          </span>
        </div>
      </div>
      <button
        onClick={onRemover}
        aria-label={`Remover ${i.nome}`}
        className="self-start rounded-md p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-danger-500"
      >
        <Trash2 size={15} strokeWidth={1.75} />
      </button>
    </li>
  );
}

function EnderecoEntrega({
  entrega,
  onMudar,
}: {
  entrega: EntregaVenda;
  onMudar: (e: EntregaVenda["endereco"]) => void;
}) {
  const outro = entrega.endereco !== null;
  const e = entrega.endereco ?? {};
  const campo = (k: keyof NonNullable<EntregaVenda["endereco"]>, rotulo: string, classe = "") => (
    <Rotulo texto={rotulo}>
      <Campo
        value={e[k] ?? ""}
        onChange={(ev) => onMudar({ ...e, [k]: ev.target.value })}
        className={classe}
      />
    </Rotulo>
  );
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={!outro}
          onChange={(ev) => onMudar(ev.target.checked ? null : {})}
          className="size-4 accent-[var(--color-gold-500)]"
        />
        Entregar no endereço do cadastro
      </label>
      {outro && (
        <div className="grid grid-cols-2 gap-2">
          {campo("cep", "CEP")}
          {campo("numero", "Número")}
          <div className="col-span-2">{campo("logradouro", "Rua")}</div>
          {campo("bairro", "Bairro")}
          {campo("cidade", "Cidade")}
          {campo("complemento", "Complemento")}
          {campo("uf", "UF")}
        </div>
      )}
    </div>
  );
}

function PedidosCriados({ pedidos }: { pedidos: Venda[] }) {
  return (
    <section aria-label="Pedidos desta conversa" className="space-y-2">
      <h3 className="text-sm">Pedidos desta conversa</h3>
      <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
        {pedidos.map((v) => {
          const s = v.pedido ? STATUS_PEDIDO[v.pedido.status] : null;
          return (
            <li key={v.id} className="flex items-center justify-between gap-2 px-3 py-2.5 text-sm">
              <div>
                <p className="font-semibold text-neutral-900">#{v.pedido?.numero}</p>
                <p className="text-xs text-neutral-500">
                  {v.itens.reduce((n, i) => n + i.quantidade, 0)} peça(s) ·{" "}
                  {formatarData(v.atualizadoEm)}
                  {v.criadoPor ? ` · ${v.criadoPor.nome}` : ""}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="font-display font-bold text-neutral-900">
                  {formatarMoeda(v.pedido?.total ?? v.total)}
                </span>
                {s && v.pedido && <Selo tom={s.tom}>{ROTULO_STATUS_PEDIDO[v.pedido.status]}</Selo>}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function Foto({ url, nome }: { url: string | null; nome: string }) {
  return url ? (
    <img
      src={url}
      alt={nome}
      loading="lazy"
      className="aspect-[4/5] w-14 shrink-0 rounded-md bg-gold-200 object-cover"
    />
  ) : (
    <span
      aria-hidden
      className="flex aspect-[4/5] w-14 shrink-0 items-center justify-center rounded-md bg-gold-200"
    >
      <img src="/logo-yd-monogram.png" alt="" className="size-7 opacity-60" />
    </span>
  );
}

function LinhaTotal({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <div className="flex justify-between text-neutral-600">
      <span>{rotulo}</span>
      <span className="font-semibold text-neutral-800">{formatarMoeda(valor)}</span>
    </div>
  );
}

/** Valor em reais com vírgula; guarda o texto enquanto a pessoa digita. */
function CampoValor({
  valor,
  onMudar,
  invalido,
}: {
  valor: number;
  onMudar: (n: number) => void;
  invalido?: boolean;
}) {
  const [texto, setTexto] = useState(valor ? String(valor).replace(".", ",") : "");
  const anterior = useMemo(() => valor, [valor]);
  useEffect(() => {
    const atual = Number(texto.replace(/\./g, "").replace(",", ".")) || 0;
    if (Math.abs(atual - anterior) > 0.001)
      setTexto(anterior ? String(anterior).replace(".", ",") : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só reage a mudança externa do valor
  }, [anterior]);
  return (
    <Campo
      value={texto}
      inputMode="decimal"
      aria-invalid={invalido}
      className={cn(invalido && "border-danger-500")}
      onChange={(e) => {
        const t = e.target.value.replace(/[^\d,]/g, "");
        setTexto(t);
        onMudar(Math.round((Number(t.replace(",", ".")) || 0) * 100) / 100);
      }}
    />
  );
}

/** Cartões enviados a esta cliente: cliques no link e compras no site. */
function ProdutosEnviados({ conversaId }: { conversaId: string }) {
  const enviados = useProdutosEnviados(conversaId);
  if (!enviados.data?.length) return null;
  return (
    <section aria-label="Produtos enviados" className="space-y-2">
      <h3 className="text-sm">Produtos enviados à cliente</h3>
      <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
        {enviados.data.map((e) => (
          <li key={e.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
            <div className="min-w-0">
              <p className="truncate font-semibold text-neutral-900">{e.nome}</p>
              <p className="text-xs text-neutral-500">
                {formatarData(e.enviadoEm)}
                {e.vendedora ? ` · ${e.vendedora}` : ""}
                {e.temLink
                  ? ` · ${e.cliques ? `${e.cliques} clique${e.cliques > 1 ? "s" : ""}` : "sem clique"}`
                  : " · sem link"}
              </p>
            </div>
            {e.pedido && (
              <Selo tom="sucesso">
                Comprou no site #{e.pedido.numero}
                {e.pedido.total !== null ? ` · ${formatarMoeda(e.pedido.total)}` : ""}
              </Selo>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Vendedora do pedido no ERP. Vem marcada a vinculada a quem atende
 * (Configurações → Integrações); trocar grava a escolha só nesta venda.
 */
function CampoVendedora({
  valor,
  opcoes,
  onMudar,
}: {
  valor: string | null;
  opcoes: OpcoesVenda | undefined;
  onMudar: (v: string | null) => void;
}) {
  const vinculada = opcoes?.vendedoraVinculada.erpId ?? null;
  const selecionada = valor ?? vinculada ?? "";
  const lista = opcoes?.vendedoras ?? [];
  const fora = selecionada && !lista.some((v) => v.id === selecionada);
  const ajuda = !vinculada
    ? `${opcoes?.vendedoraVinculada.atendente ?? "Quem atende"} ainda não tem vendedora do ERP vinculada (Configurações → Integrações).`
    : valor && valor !== vinculada
      ? "Trocada só nesta venda."
      : `Vinculada a ${opcoes?.vendedoraVinculada.atendente ?? "quem atende"}.`;
  return (
    <Rotulo texto="Vendedora no ERP" ajuda={ajuda}>
      <Seletor
        value={selecionada}
        // Escolher a vinculada volta ao automático (segue quem atende).
        onChange={(e) =>
          onMudar(!e.target.value || e.target.value === vinculada ? null : e.target.value)
        }
      >
        {!selecionada && <option value="">Sem vendedora</option>}
        {fora && <option value={selecionada}>Vendedora atual</option>}
        {lista.map((v) => (
          <option key={v.id} value={v.id}>
            {v.nome}
          </option>
        ))}
      </Seletor>
    </Rotulo>
  );
}
